import { useCallback, useEffect, useMemo, useState } from 'react';
import { announcementsApiUrl } from '../utils/runtime';
import { useOptionalAppSession } from './AppSessionContext';

const API_BASE = announcementsApiUrl || '/wp-json/pfandpirat/v1/announcements';

function getNonce() {
  return window?.pfandpiratData?.nonce || '';
}

async function parseJson(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error('Antwort vom Neuigkeiten-Endpunkt war kein gültiges JSON.');
  }
}

export function useAnnouncements() {
  const sessionContext = useOptionalAppSession();
  const bootstrapAnnouncements = sessionContext?.session?.announcements || null;
  const hasBootstrapAnnouncements = Array.isArray(bootstrapAnnouncements?.items);
  const [items, setItems] = useState(() => (hasBootstrapAnnouncements ? bootstrapAnnouncements.items : []));
  const [unreadCount, setUnreadCount] = useState(() => Number(bootstrapAnnouncements?.unread_count || 0));
  const [loading, setLoading] = useState(!hasBootstrapAnnouncements);
  const [error, setError] = useState(null);

  const syncAnnouncementsToSession = useCallback((nextItems, nextUnreadCount) => {
    if (!sessionContext?.mergeSessionPatch) return;

    sessionContext.mergeSessionPatch({
      announcements: {
        items: Array.isArray(nextItems) ? nextItems : [],
        unread_count: Number(nextUnreadCount || 0),
      },
    });
  }, [sessionContext]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [itemsRes, countRes] = await Promise.all([
        fetch(API_BASE, {
          credentials: 'include',
          cache: 'no-store',
          headers: { 'X-WP-Nonce': getNonce(), 'Cache-Control': 'no-cache' },
        }),
        fetch(`${API_BASE}/unread-count`, {
          credentials: 'include',
          cache: 'no-store',
          headers: { 'X-WP-Nonce': getNonce(), 'Cache-Control': 'no-cache' },
        }),
      ]);

      if (!itemsRes.ok) {
        throw new Error('Neuigkeiten konnten nicht geladen werden.');
      }
      if (!countRes.ok) {
        throw new Error('Ungelesene Neuigkeiten konnten nicht geladen werden.');
      }

      const [itemsData, countData] = await Promise.all([parseJson(itemsRes), parseJson(countRes)]);
      const nextItems = Array.isArray(itemsData) ? itemsData : [];
      const nextUnreadCount = Number(countData?.count || 0);
      setItems(nextItems);
      setUnreadCount(nextUnreadCount);
      syncAnnouncementsToSession(nextItems, nextUnreadCount);
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [syncAnnouncementsToSession]);

  useEffect(() => {
    if (hasBootstrapAnnouncements) {
      setItems(bootstrapAnnouncements.items);
      setUnreadCount(Number(bootstrapAnnouncements.unread_count || 0));
      setLoading(false);
      return;
    }

    refresh();
  }, [hasBootstrapAnnouncements, bootstrapAnnouncements, refresh]);

  const markRead = useCallback(async (announcementId) => {
    await fetch(`${API_BASE}/${announcementId}/read`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-WP-Nonce': getNonce() },
    });

    let decreased = false;
    let nextItems = [];
    setItems((prev) => {
      nextItems = prev.map((item) => {
        if (Number(item.id) !== Number(announcementId)) {
          return item;
        }
        if (!item.is_read) {
          decreased = true;
        }
        return { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() };
      });
      return nextItems;
    });
    if (decreased) {
      setUnreadCount((prev) => {
        const nextUnreadCount = Math.max(0, prev - 1);
        syncAnnouncementsToSession(nextItems, nextUnreadCount);
        return nextUnreadCount;
      });
    }
  }, [syncAnnouncementsToSession]);

  const dismiss = useCallback(async (announcementId) => {
    await fetch(`${API_BASE}/${announcementId}/dismiss`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-WP-Nonce': getNonce() },
    });

    let decreased = false;
    let nextItems = [];
    setItems((prev) => {
      nextItems = prev.map((item) => {
        if (Number(item.id) !== Number(announcementId)) {
          return item;
        }
        if (!item.is_read) {
          decreased = true;
        }
        return {
          ...item,
          is_read: true,
          is_dismissed: true,
          read_at: item.read_at || new Date().toISOString(),
          dismissed_at: item.dismissed_at || new Date().toISOString(),
        };
      });
      return nextItems;
    });
    if (decreased) {
      setUnreadCount((prev) => {
        const nextUnreadCount = Math.max(0, prev - 1);
        syncAnnouncementsToSession(nextItems, nextUnreadCount);
        return nextUnreadCount;
      });
    } else {
      syncAnnouncementsToSession(nextItems, unreadCount);
    }
  }, [syncAnnouncementsToSession, unreadCount]);

  const bannerAnnouncements = useMemo(
    () => items.filter((item) => item.placement === 'banner' && !item.is_dismissed),
    [items]
  );

  const modalAnnouncement = useMemo(
    () => items.find((item) => item.placement === 'modal' && !item.is_dismissed),
    [items]
  );

  const centerAnnouncements = useMemo(
    () => items.filter((item) => item.placement === 'center' || item.placement === 'push'),
    [items]
  );

  return {
    announcements: items,
    centerAnnouncements,
    bannerAnnouncements,
    modalAnnouncement,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    dismiss,
  };
}
